import { useState, useEffect, useMemo, SetStateAction } from 'react';
import { Table, Pagination, Button, Image } from '@bfe/moly';
import BigNumber, { toFormatUnit } from '@fe/utils/number';
import { useStakingStore } from '@/store/staking';
import { sortUp, sortDown } from '@/utils/sort';
import { Event, Page } from '@/config/track-config';
import { scTrack } from '@web3/base-tracking';
import SortBtn from '@/components/sortBtn';
import { useFm } from '@web3/base-hooks';
import { sliceTableData } from '@/utils/util';
import { SORT_TYPE_MAP, DEFAULT_VAL } from '@/utils/constants';
import { PoolItem, NetworkItem } from '@/types/staking';
import cls from 'classnames';
import { RowTitle } from './RowTitle';

import styles from './index.module.less';
import commonStyles from '../index.module.less';

interface IProps {
  poolList: PoolItem[];
  networkList: NetworkItem[];
  network: string;
  currentTab: string;
  handleStakingClick: (params: { id: number; poolCode: string }) => void;
}

const PAGE_SIZE = 10;

const DEFAULT_SORT_MAP = {
  apy: SORT_TYPE_MAP.NORMAL,
  tvl: SORT_TYPE_MAP.NORMAL,
};

export const ColBox = ({
  poolList,
  network,
  networkList,
  currentTab,
  handleStakingClick,
}: IProps) => {
  const fm = useFm();
  const { poolLoading } = useStakingStore();
  const [currentPage, setCurrentPage] = useState(1);
  const [sortMap, setSortMap] = useState(DEFAULT_SORT_MAP);

  const onPageChange = (page: number) => {
    setCurrentPage(page);
  };

  // 用于分页的总数据
  const allData = useMemo(() => {
    if (network) {
      return poolList.filter((item) => item.chainCode === network);
    }
    return poolList;
  }, [network, poolList]);

  // 当页展示数据
  const data = useMemo(() => {
    let formatData = allData.slice();
    // 筛选排序
    Object.keys(sortMap).some((item) => {
      const sortType = sortMap[item as keyof typeof sortMap];
      if (sortType !== 'NORMAL') {
        formatData =
          sortType === 'UP' ? sortUp(allData, item) : sortDown(allData, item);
      }
    });

    // network排序
    if (network) {
      const networkData = formatData.filter(
        (item) => item.chainCode === network
      );
      return sliceTableData(networkData, currentPage, PAGE_SIZE) ?? [];
    }

    return sliceTableData(formatData, currentPage, PAGE_SIZE) ?? [];
  }, [allData, currentPage, network, sortMap]);

  const handleSort = (columnKey: string, sortType: SetStateAction<string>) => {
    scTrack(Event.Web3UserClick, {
      page_name: Page.StakingWebHomepage,
      sort_name: `${columnKey}${sortType}`,
    });

    setSortMap({ ...DEFAULT_SORT_MAP, [columnKey]: sortType });
  };

  const columns: any = [
    {
      header: fm('staking_project_name'),
      accessorKey: 'symbolAlias',
      cell: ({ row }: any) => {
        const {
          icon,
          poolName,
          poolType,
          poolCode,
          baseStakeCoins,
          chainCode,
          poolExtendDTO,
          noticeConfigDTO,
          poolIcons,
        } = row?.original || {};

        return (
          <RowTitle
            icon={icon.night}
            poolName={poolName}
            poolCode={poolCode}
            poolType={poolType}
            chainCode={chainCode}
            isNew={poolExtendDTO?.isNew}
            isHot={poolExtendDTO?.isHot}
            noticeContent={noticeConfigDTO?.content}
            containerClass={styles.infoContainerClass}
            baseStakeCoins={baseStakeCoins}
            poolIcons={poolIcons}
          />
        );
      },
    },
    {
      header: fm('network'),
      accessorKey: 'network',
      cell: ({ row }: any) => {
        const { chainCode = '' } = row?.original || {};

        const networkData: NetworkItem | any =
          networkList.find((item) => item.chainCode === chainCode) || [];

        if (!networkData?.iconNightUrl) return DEFAULT_VAL;

        return (
          <div className={cls('flex justify-end', commonStyles.networkImg)}>
            <Image
              className={cls(styles.icon)}
              src={networkData?.iconNightUrl}
              style={{
                height: 24,
                width: 24,
              }}
              alt=""
            />
          </div>
        );
      },
    },
    {
      header: (
        <SortBtn
          txt={fm('tvl')}
          sortType={sortMap.tvl}
          sortColumn="tvl"
          onSort={handleSort}
        />
      ),
      accessorKey: 'tvl',
      cell: ({ row }: any) => {
        const { tvl = '' } = row?.original || {};

        return (
          <p className={cls('text-right', styles.tvl)}>
            ${toFormatUnit(tvl, 2)}
          </p>
        );
      },
    },
    {
      header: (
        <SortBtn
          txt={fm('apy')}
          sortType={sortMap.apy}
          sortColumn="apy"
          onSort={handleSort}
        />
      ),
      accessorKey: 'apy',
      cell: ({ row }: any) => {
        const { apy = '' } = row?.original || {};

        return (
          <p className={cls(styles.apy, commonStyles.apy)}>
            +{`${BigNumber(apy).toFixed(2)}%`}
          </p>
        );
      },
    },
    {
      header: fm('earn'),
      accessorKey: 'earn',
      cell: ({ row }: any) => {
        const { poolExtendDTO = '' } = row?.original || {};

        return (
          <p className={cls(styles.earn)}>
            {poolExtendDTO?.poolLabelDTO?.rewardMsg || DEFAULT_VAL}
          </p>
        );
      },
    },
    {
      header: fm('action'),
      accessorKey: 'action',
      cell: ({ row }: { row: any }) => {
        return (
          <Button
            size="xx-small"
            variant="secondary"
            className={cls(styles.investBtn)}
            onClick={() => onRowClick(row?.original)}
          >
            {fm('staking')}
          </Button>
        );
      },
    },
  ];

  const onRowClick = (targetItem: any) => {
    handleStakingClick(targetItem);
  };

  useEffect(() => {
    // tab 或 network 或 排序切换时 重置页脚
    setCurrentPage(1);
  }, [currentTab, network, sortMap]);

  return (
    <div
      className={cls('flex flex-wrap', styles.tableContainer)}
      key={currentTab}
    >
      <Table
        columns={columns}
        loading={poolLoading}
        data={data}
        emptyText={fm('no_data')}
      />
      {Boolean(poolList?.length) && (
        <div
          className={cls(
            'w-full flex justify-center items-center',
            commonStyles.paginationContainer
          )}
        >
          <Pagination
            current={currentPage}
            pageSize={PAGE_SIZE}
            onChange={onPageChange}
            total={allData?.length || 0}
          />
        </div>
      )}
    </div>
  );
};
